import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "../lib/api";
import { Copy, RefreshCw, Plus, Trash2, Edit, ExternalLink, AlertTriangle, CheckCircle2 } from "lucide-react";

interface PlatformListing {
  id: string;
  userId: string;
  externalId: string;
  title: string;
  description: string;
  price: number;
  category: string;
  serviceType: string | null;
  address: string;
  contactPhone: string | null;
  managerName: string | null;
  contactMethod: string;
  images: string[];
  isActive: boolean;
  avitoAdId: string | null;
  avitoStatus: string | null;
  avitoUrl: string | null;
  views: number;
  contacts: number;
  lastSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ListingsResponse {
  listings: PlatformListing[];
  hasCredentials: boolean;
  feedUrl: string;
}

type DraftListing = {
  userId: string;
  externalId: string;
  title: string;
  description: string;
  price: number;
  category: string;
  serviceType: string;
  address: string;
  contactPhone: string;
  managerName: string;
  contactMethod: string;
  images: string;
  isActive: boolean;
};

const emptyDraft = (userId: string): DraftListing => ({
  userId,
  externalId: "",
  title: "",
  description: "",
  price: 0,
  category: "Предложения услуг",
  serviceType: "",
  address: "",
  contactPhone: "",
  managerName: "",
  contactMethod: "По телефону и в сообщениях",
  images: "",
  isActive: true,
});

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  active: { label: "Активно", className: "bg-green-100 text-green-700" },
  rejected: { label: "Отклонено", className: "bg-red-100 text-red-700" },
  blocked: { label: "Заблокировано", className: "bg-red-100 text-red-700" },
  expired: { label: "Истекло", className: "bg-gray-100 text-gray-500" },
  old: { label: "Архив", className: "bg-gray-100 text-gray-500" },
  removed: { label: "Снято", className: "bg-gray-100 text-gray-500" },
};

function StatusBadge({ status }: { status: string | null }) {
  if (!status) {
    return (
      <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700">
        Ожидает
      </span>
    );
  }
  const item = STATUS_LABELS[status] || { label: status, className: "bg-gray-100 text-gray-600" };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${item.className}`}>
      {item.label}
    </span>
  );
}

export default function AvitoPlatformListingsPage() {
  const [userIdInput, setUserIdInput] = useState<string>(() => localStorage.getItem("avitoPlatformUserId") || "");
  const [activeUserId, setActiveUserId] = useState<string>(userIdInput);
  const [draft, setDraft] = useState<DraftListing | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const queryKey = activeUserId
    ? `/api/admin/avito-platform-listings?userId=${encodeURIComponent(activeUserId)}`
    : null;

  const { data, isLoading, error } = useQuery<ListingsResponse>({
    queryKey: queryKey ? [queryKey] : ["avito-platform-listings-disabled"],
    enabled: !!queryKey,
  });

  const createMutation = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const res = await apiRequest("POST", "/api/admin/avito-platform-listings", body);
      return res.json();
    },
    onSuccess: () => {
      if (queryKey) queryClient.invalidateQueries({ queryKey: [queryKey] });
      setDraft(null);
      setEditingId(null);
    },
    onError: (err: Error) => alert(`Ошибка создания: ${err.message}`),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) => {
      const res = await apiRequest("PUT", `/api/admin/avito-platform-listings/${id}`, body);
      return res.json();
    },
    onSuccess: () => {
      if (queryKey) queryClient.invalidateQueries({ queryKey: [queryKey] });
      setDraft(null);
      setEditingId(null);
    },
    onError: (err: Error) => alert(`Ошибка сохранения: ${err.message}`),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/admin/avito-platform-listings/${id}`);
    },
    onSuccess: () => {
      if (queryKey) queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
  });

  const syncMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/admin/avito-platform-listings/sync?userId=${encodeURIComponent(activeUserId)}`,
      );
      return res.json() as Promise<{ matched: number; unmatched: number; skipped: boolean }>;
    },
    onSuccess: (result) => {
      if (queryKey) queryClient.invalidateQueries({ queryKey: [queryKey] });
      alert(`Синхронизация: найдено ${result.matched}, не найдено ${result.unmatched}`);
    },
    onError: (err: Error) => alert(`Ошибка синхронизации: ${err.message}`),
  });

  const handleLoad = () => {
    setActiveUserId(userIdInput);
    localStorage.setItem("avitoPlatformUserId", userIdInput);
  };

  const handleStartCreate = () => {
    setDraft(emptyDraft(activeUserId));
    setEditingId(null);
  };

  const handleStartEdit = (listing: PlatformListing) => {
    setDraft({
      userId: listing.userId,
      externalId: listing.externalId,
      title: listing.title,
      description: listing.description,
      price: listing.price,
      category: listing.category,
      serviceType: listing.serviceType || "",
      address: listing.address,
      contactPhone: listing.contactPhone || "",
      managerName: listing.managerName || "",
      contactMethod: listing.contactMethod,
      images: (listing.images || []).join("\n"),
      isActive: listing.isActive,
    });
    setEditingId(listing.id);
  };

  const handleSubmit = () => {
    if (!draft) return;
    const body = {
      userId: draft.userId,
      externalId: draft.externalId.trim(),
      title: draft.title.trim(),
      description: draft.description.trim(),
      price: Number(draft.price) || 0,
      category: draft.category.trim() || "Предложения услуг",
      serviceType: draft.serviceType.trim() || null,
      address: draft.address.trim(),
      contactPhone: draft.contactPhone.trim() || null,
      managerName: draft.managerName.trim() || null,
      contactMethod: draft.contactMethod.trim() || "По телефону и в сообщениях",
      images: draft.images
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
      isActive: draft.isActive,
    };
    if (editingId) {
      updateMutation.mutate({ id: editingId, body });
    } else {
      createMutation.mutate(body);
    }
  };

  const copyFeedUrl = () => {
    if (data?.feedUrl) {
      navigator.clipboard.writeText(data.feedUrl);
      alert("URL скопирован");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Avito Platform Listings</h1>
      </div>

      <div className="rounded-md border border-border p-4 space-y-3">
        <div className="text-sm text-muted-foreground">
          Управление объявлениями платформы на Avito через Autoload XML-feed. Укажи User ID владельца
          (GPT Manager user.id), чтобы загрузить его объявления. Avito подхватит фид по публичной ссылке.
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="User ID..."
            value={userIdInput}
            onChange={(e) => setUserIdInput(e.target.value)}
            className="flex-1 px-3 py-2 border rounded-md text-sm font-mono"
          />
          <button
            onClick={handleLoad}
            disabled={!userIdInput.trim()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium disabled:opacity-50"
          >
            Загрузить
          </button>
        </div>

        {data && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <div className="flex-1 font-mono bg-muted/50 px-3 py-2 rounded text-xs">
                {data.feedUrl}
              </div>
              <button
                onClick={copyFeedUrl}
                className="p-2 hover:bg-muted rounded"
                title="Скопировать URL"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            {data.hasCredentials ? (
              <div className="flex items-center gap-2 text-sm text-green-700">
                <CheckCircle2 className="w-4 h-4" />
                Avito credentials подключены — синхронизация статистики доступна
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-yellow-700">
                <AlertTriangle className="w-4 h-4" />
                Avito credentials не найдены у пользователя. Подключи Avito-канал к любому боту юзера для синка статистики.
              </div>
            )}
          </div>
        )}
      </div>

      {activeUserId && (
        <div className="flex gap-2">
          <button
            onClick={handleStartCreate}
            className="inline-flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Добавить объявление
          </button>
          <button
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending || !data?.hasCredentials}
            className="inline-flex items-center gap-2 px-3 py-2 border rounded-md text-sm font-medium disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${syncMutation.isPending ? "animate-spin" : ""}`} />
            Синхронизировать с Avito
          </button>
        </div>
      )}

      {draft && (
        <div className="rounded-md border border-border p-4 space-y-3 bg-muted/20">
          <div className="font-semibold text-sm">
            {editingId ? "Редактировать объявление" : "Новое объявление"}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <LabeledInput
              label="External ID (стабильный, напр. ai-bot-setup-1)"
              value={draft.externalId}
              disabled={!!editingId}
              onChange={(v) => setDraft({ ...draft, externalId: v })}
            />
            <LabeledInput
              label="Цена (₽)"
              type="number"
              value={String(draft.price)}
              onChange={(v) => setDraft({ ...draft, price: Number(v) || 0 })}
            />
            <LabeledInput
              label="Заголовок (до 50 символов)"
              value={draft.title}
              onChange={(v) => setDraft({ ...draft, title: v })}
            />
            <LabeledInput
              label="Category"
              value={draft.category}
              onChange={(v) => setDraft({ ...draft, category: v })}
            />
            <LabeledInput
              label="ServiceType (подкатегория услуг)"
              value={draft.serviceType}
              onChange={(v) => setDraft({ ...draft, serviceType: v })}
            />
            <LabeledInput
              label="Address"
              value={draft.address}
              onChange={(v) => setDraft({ ...draft, address: v })}
            />
            <LabeledInput
              label="Contact Phone"
              value={draft.contactPhone}
              onChange={(v) => setDraft({ ...draft, contactPhone: v })}
            />
            <LabeledInput
              label="Manager Name"
              value={draft.managerName}
              onChange={(v) => setDraft({ ...draft, managerName: v })}
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Description</label>
            <textarea
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              rows={6}
              className="w-full px-3 py-2 border rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">
              Images (по одному URL на строку, до 10)
            </label>
            <textarea
              value={draft.images}
              onChange={(e) => setDraft({ ...draft, images: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border rounded-md text-sm font-mono text-xs"
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.isActive}
              onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
            />
            Активно (включено в feed)
          </label>
          <div className="flex gap-2">
            <button
              onClick={handleSubmit}
              disabled={createMutation.isPending || updateMutation.isPending}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium disabled:opacity-50"
            >
              {editingId ? "Сохранить" : "Создать"}
            </button>
            <button
              onClick={() => {
                setDraft(null);
                setEditingId(null);
              }}
              className="px-4 py-2 border rounded-md text-sm"
            >
              Отмена
            </button>
          </div>
        </div>
      )}

      {isLoading && <div className="text-muted-foreground text-sm">Загрузка...</div>}
      {error && <div className="text-red-600 text-sm">{(error as Error).message}</div>}

      {data && data.listings.length === 0 && (
        <div className="text-muted-foreground text-sm">Объявлений пока нет.</div>
      )}

      {data && data.listings.length > 0 && (
        <div className="border rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr>
                <th className="text-left px-3 py-2 font-medium">External ID</th>
                <th className="text-left px-3 py-2 font-medium">Заголовок</th>
                <th className="text-right px-3 py-2 font-medium">Цена</th>
                <th className="text-center px-3 py-2 font-medium">Feed</th>
                <th className="text-center px-3 py-2 font-medium">Avito</th>
                <th className="text-right px-3 py-2 font-medium">Views</th>
                <th className="text-right px-3 py-2 font-medium">Контакты</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.listings.map((l) => (
                <tr key={l.id} className="border-t">
                  <td className="px-3 py-2 font-mono text-xs">{l.externalId}</td>
                  <td className="px-3 py-2">
                    <div>{l.title}</div>
                    <div className="text-xs text-muted-foreground">{l.serviceType || l.category}</div>
                  </td>
                  <td className="px-3 py-2 text-right">{l.price.toLocaleString("ru-RU")} ₽</td>
                  <td className="px-3 py-2 text-center">
                    {l.isActive ? (
                      <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                        В фиде
                      </span>
                    ) : (
                      <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                        Скрыто
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <StatusBadge status={l.avitoStatus} />
                    {l.avitoUrl && (
                      <a
                        href={l.avitoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 inline-block text-blue-600"
                      >
                        <ExternalLink className="w-3.5 h-3.5 inline" />
                      </a>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">{l.views}</td>
                  <td className="px-3 py-2 text-right">{l.contacts}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => handleStartEdit(l)}
                      className="p-1.5 hover:bg-muted rounded"
                      title="Редактировать"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Удалить объявление "${l.title}"?`)) {
                          deleteMutation.mutate(l.id);
                        }
                      }}
                      className="p-1.5 hover:bg-muted rounded text-red-600"
                      title="Удалить"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  type = "text",
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  disabled?: boolean;
}) {
  return (
    <div>
      <label className="block text-xs text-muted-foreground mb-1">{label}</label>
      <input
        type={type}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border rounded-md text-sm disabled:bg-muted/30"
      />
    </div>
  );
}
